import React from 'react';
import { Header, Message, Segment } from 'semantic-ui-react';

import Button from '../../../../components/Button';
import { StandaloneChargeActivationStatusDTO, StandaloneChargeActivationStateEnum } from '../../../../services/standalone-charges';

import ActivationWizardModal from './ActivationWizardModal';
import { ActivationFormState, ActivationStep } from '../../types';

type Props = {
  activationLoading: boolean;
  activationStatusError: boolean;
  activationStatus: StandaloneChargeActivationStatusDTO | null;
  activationIsPendingKyc: boolean;
  activationKycLink: string;
  activationKycPageUrl: string;
  activationKycQrImageUrl: string;
  openKycLink: () => void;
  loadActivationStatus: () => void;

  activationWizardOpen: boolean;
  closeActivationWizard: () => void;
  openActivationWizard: () => void;

  activationStep: ActivationStep;
  lastActivationStep: number;
  activationSaving: boolean;
  activationStepError: string;
  recipientType: ActivationFormState['recipientType'];
  handleBackActivationStep: () => void;
  handleNextActivationStep: () => void;
  handleActivate: () => void;

  children: React.ReactNode;
};

const ActivationGate: React.FC<Props> = ({
  activationLoading,
  activationStatusError,
  activationStatus,
  activationIsPendingKyc,
  activationKycLink,
  activationKycPageUrl,
  activationKycQrImageUrl,
  openKycLink,
  loadActivationStatus,
  activationWizardOpen,
  closeActivationWizard,
  openActivationWizard,
  activationStep,
  lastActivationStep,
  activationSaving,
  activationStepError,
  recipientType,
  handleBackActivationStep,
  handleNextActivationStep,
  handleActivate,
  children
}) => {
  if (activationLoading) {
    return (
      <Segment>
        <Header as="h3" style={{ marginTop: 0 }}>
          Carregando...
        </Header>
      </Segment>
    );
  }

  const activationStatusValue = activationStatus?.activation?.status;
  const canStartActivation =
    !activationStatusValue ||
    activationStatusValue === StandaloneChargeActivationStateEnum.notStarted ||
    activationStatusValue === StandaloneChargeActivationStateEnum.pagarmeRejected;

  return (
    <Segment>
      <Header as="h3" style={{ marginTop: 0 }}>
        Ative Cobranças para sua imobiliária
      </Header>

      {canStartActivation && (
        <>
          <p style={{ marginBottom: 12 }}>
            Para emitir cobranças, você precisa concluir o cadastro de ativação.
          </p>
          <div style={{ marginBottom: 16 }}>
            <Button type="button" text="Iniciar ativação" onClick={openActivationWizard} />
          </div>
        </>
      )}

      {activationStatusError && (
        <Message warning>
          <Message.Header>Não conseguimos verificar o status agora</Message.Header>
          <p>Tente novamente para atualizar o status da ativação.</p>
          <Button type="button" text="Tentar novamente" onClick={loadActivationStatus} />
        </Message>
      )}

      {activationStatus?.activation?.lastSyncError && (
        <Message negative>
          <Message.Header>Falha no cadastro do recebedor na Pagar.me</Message.Header>
          <p>{activationStatus.activation.lastSyncError}</p>
        </Message>
      )}

      {activationIsPendingKyc && (
        <Message info>
          <Message.Header>Biometria pendente na Pagar.me</Message.Header>
          <p style={{ marginBottom: 12 }}>
            Seu cadastro foi enviado, mas a emissão de cobranças permanece bloqueada até a
            conclusão da prova de vida.
          </p>
          {activationKycPageUrl && (
            <p style={{ marginBottom: 12 }}>
              <a href={activationKycPageUrl} target="_blank" rel="noopener noreferrer">
                Abrir página de biometria
              </a>
            </p>
          )}
          {activationKycLink && (
            <p style={{ marginBottom: 12 }}>
              <a href={activationKycLink} target="_blank" rel="noopener noreferrer">
                Abrir link direto da Pagar.me
              </a>
            </p>
          )}
          {activationKycQrImageUrl && (
            <div style={{ marginBottom: 12 }}>
              <img
                src={activationKycQrImageUrl}
                alt="QR Code para biometria na Pagar.me"
                style={{ width: 180, height: 180, border: '1px solid #ddd', padding: 4 }}
              />
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button type="button" text="Abrir biometria" onClick={openKycLink} />
            <Button
              type="button"
              text="Atualizar status"
              light
              onClick={loadActivationStatus}
              loading={activationLoading}
              disabled={activationLoading}
            />
          </div>
        </Message>
      )}

      <ActivationWizardModal
        open={activationWizardOpen}
        onClose={closeActivationWizard}
        activationStep={activationStep}
        lastActivationStep={lastActivationStep}
        activationSaving={activationSaving}
        activationStepError={activationStepError}
        recipientType={recipientType}
        onBack={handleBackActivationStep}
        onNext={handleNextActivationStep}
        onFinish={handleActivate}
      >
        {children}
      </ActivationWizardModal>
    </Segment>
  );
};

export default ActivationGate;

